/**
 * 簡略版スクロールアニメーション登録関数
 * @param {Object} config - アニメーション設定オブジェクト
 * @param {string} config.trigger - 親コンテナのIDまたはクラス（セレクタ）
 * @param {Array} config.childrens - 子要素の設定配列
 * @param {string} config.childrens[].elementName - 要素のIDまたはクラス（セレクタ）
 * @param {string|null} config.childrens[].fadeInClass - フェードイン用のクラス名
 * @param {number} config.childrens[].fadeInDelay - クラス付与の遅延時間（秒）前の要素に加算
 * @param {boolean} config.childrens[].isMoveToUp - スクロールに応じて上に移動するアニメーションの有無
 */
function initScrollAnimation2(config) {
  const triggerElement = gsap.utils.toArray(config.trigger)[0];

  if (!triggerElement) {
    console.warn(`Trigger element not found: ${config.trigger}`);
    return;
  }

  // 累積遅延時間を計算
  let accumulatedDelay = 0;

  config.childrens.forEach((child, index) => {
    const element = gsap.utils.toArray(child.elementName, triggerElement)[0];

    if (!element) {
      console.warn(`Child element not found: ${child.elementName}`);
      return;
    }

    // 遅延時間を累積
    accumulatedDelay += child.fadeInDelay || 0;

    // trigger起点でのフェードインアニメーション
    if (child.fadeInClass) {
      const currentDelay = accumulatedDelay; 
      ScrollTrigger.create({
        trigger: triggerElement,
        start: 'top 80%',
        once: true,
        onEnter: () => {
          setTimeout(() => {
            element.classList.add(child.fadeInClass);
          }, currentDelay * 1000); // 秒をミリ秒に変換
        }
      });
    }

    // 要素自身をトリガーとするスクロール連動の上昇アニメーション
    if (child.isMoveToUp) {
      gsap.to(element, {
        y: -200,
        ease: 'none',
        scrollTrigger: {
          trigger: element, 
          start: 'top bottom',
          end: 'bottom+=100 top',
          scrub: 1,
          toggleActions: 'play none none reverse'
        }
      });
    }
  });
}

/**
 * ページ最上部にスムーススクロールする関数
 * @param {number} duration - スクロールにかける時間（ミリ秒）
 */
function scrollToTop(duration) {
  const start = window.pageYOffset || document.documentElement.scrollTop;
  const startTime = performance.now();

  const scroll=(currentTime)=>{
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // easeInOutQuad イージング
    const easing = progress < 0.5
      ? 2 * progress * progress
      : 1 - (-2 * progress + 2)**2 / 2;

    window.scrollTo(0, start * (1 - easing));

    if (progress < 1) {
      requestAnimationFrame(scroll);
    }
  }

  requestAnimationFrame(scroll);
}

/**
 * Canvas2Dでベジェ曲線による波打つ円アニメーションを描画
 * @param {string} containerId - canvasを挿入するコンテナのセレクタ
 * @param {string} color - 塗りつぶし色（カラーコード）
 * @param {Array} sizes - レスポンシブ設定の配列 [{size: int, deviceWidth: int}]
 */
function createCanvasWavyCircle({containerId, color, sizes}) {
  const container = document.querySelector(containerId);

  if (!container) {
    console.warn(`Container not found: ${containerId}`);
    return;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  container.appendChild(canvas);

  const circ = (4 * (Math.sqrt(2) - 1) / 3);
  let count = Math.PI;

  // 現在の画面幅に応じたサイズを取得
  function getCurrentSize() {
    const windowWidth = window.innerWidth;
    // sizesを降順にソートして最初にマッチするものを取得
    const sorted = [...sizes].sort((a, b) => b.deviceWidth - a.deviceWidth);

    for (let i = 0; i < sorted.length; i++) {
      if (windowWidth >= sorted[i].deviceWidth) {
        return sorted[i].size;
      }
    }

    // マッチしない場合は最小のsizeを返す
    return sorted[sorted.length - 1].size;
  }

  // 初期サイズを即座に取得して設定
  let currentSize = getCurrentSize();
  let width = canvas.width = currentSize;
  let height = canvas.height = currentSize;

  // canvasサイズを更新
  function updateCanvasSize() {
    const newSize = getCurrentSize();

    if (newSize !== currentSize) {
      currentSize = newSize;
      width = canvas.width = currentSize;
      height = canvas.height = currentSize;
      // canvasサイズ変更時はコンテキストがリセットされるため再設定不要
    }
  }

  // リサイズイベント
  window.addEventListener('resize', updateCanvasSize);

  function drawBezierCircle(cx, cy, r) {
    let c;
    r /= 2;

    count += 0.03;

    ctx.translate(cx, cy);
    ctx.beginPath();

    // top right
    c = circ + (0.2 * Math.sin(count));
    ctx.moveTo(0, -r);
    ctx.bezierCurveTo(
      c * r, -r,
      r, -c * r,
      r, 0
    );

    // bottom right
    c = circ + (0.2 * Math.cos(count));
    ctx.bezierCurveTo(
      r, c * r,
      c * r, r,
      0, r
    );

    // bottom left
    c = circ + (0.2 * Math.sin(count * 2));
    ctx.bezierCurveTo(
      -c * r, r,
      -r, c * r,
      -r, 0
    );

    // top left
    c = circ + (0.2 * Math.cos(count + 1));
    ctx.bezierCurveTo(
      -r, -c * r,
      -c * r, -r,
      0, -r
    );

    ctx.fill();
  }

  function render() {
    requestAnimationFrame(render);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = color;

    // canvasサイズを円の最大直径とするため、変動を考慮して縮小
    const circleRadius = (currentSize / 2) * 0.75;
    drawBezierCircle(width / 2, height / 2, circleRadius * 2);
  }

  render();
}
